import type {
  AssistantResponseStatus,
  OutputComponent,
} from "@f2e/shared/types"

import {
  Agent,
  ChunkLocation,
  User,
  NoOwner,
  Profile,
  QueryRecord,
  QueryBundle,
  Workspace,
  AgentProfile,
  DefaultScope,
  Member,
  Connection,
  ProviderConfig,
  AgentType,
  UnifiedTool,
  ContextObj,
  ContextImage,
  FileBadgeData,
  BasicAgentMeta,
  ReasoningStep,
  ReasoningStepType,
  EvaluationCase,
  CaseEvaluatorInfosByCase,
  EvaluationResult,
  TokenUsage,
  ConversationTokenUsage,
  Evaluator,
  FileAttachmentScope,
  ApiAttachment,
  Brand,
  BrandOAuthProvider,
  ClientOAuthProvider,
  ClientChannel,
  ClientChannelListItem,
  ClientChannelOAuthBinding,
  ClientChannelType,
  ClientChannelConfig,
  WebClientConfig,
  WidgetClientConfig,
  ExtensionClientConfig,
  InstagramClientConfig,
  SlackClientConfig,
  LineClientConfig,
  MessengerClientConfig,
  DescriptionItem,
  clampOutputPresentationMaxItems,
  DEFAULT_HUMAN_HANDOFF_CONFIG,
  normalizeAiTakeoverGreeting,
  DEFAULT_OUTPUT_PRESENTATION_POLICY,
  AgentLLMModelSettings,
  AgentLLMModelSettingsMap,
  AgentThinkingLevel,
  AgentResponseMode,
  DEFAULT_AGENT_RESPONSE_MODE,
  HumanHandoffConfig,
  OUTPUT_PRESENTATION_KINDS,
  OutputPresentationKind,
  OutputPresentationPolicy,
  WidgetOriginPolicyMode,
  WidgetLoginVerificationMode,
  WidgetPosition,
  WidgetLocale,
  WidgetBaseColor,
} from "@/types"
import {
  apiAttachmentsToUploadingFiles,
  HttpRequestToolSchema,
  Text2SpeechToolSchema,
  UnifiedToolSchema,
} from "./agents"
import type { HttpRequestToolConfig } from "@/types/httpRequestTool"
import type { Text2SpeechConfig } from "@/types/text2speechTool"

export interface OrganizationSchema {
  id: string
  name: string
  avatar?: string
  owner: UserSchema
  my_permissions: UserScopePermissionsSchema
}

export interface DefaultScopeSchema {
  organization_role: string
  workspace_id: string
  workspace_role: string
}

export interface ProfileSchema {
  default_workspace_id?: string
  default_search: boolean
  default_organization_id: string
  default_scopes: Record<string, DefaultScopeSchema>
  preferred_llm?: string | null
  workspace_organization_map?: Record<string, string>
}

export interface UserSchema {
  id: number
  name: string
  email: string
  avatar: string
  profile?: ProfileSchema
}

export interface CurrentUserSchema extends UserSchema {
  has_usable_password?: boolean
  has_google_login?: boolean
}

export interface UserRoleSchema extends UserSchema {
  my_permissions: UserScopePermissionsSchema
}

export interface UserPermissionSchema {
  can_create: boolean
  can_delete: boolean
  can_edit_name: boolean
  can_edit_logo: boolean
  can_edit_resources: boolean
  can_edit_role: boolean
  can_add_members: boolean
  can_remove_members: boolean
  can_add_admins: boolean
  can_remove_admins: boolean
  can_create_connection?: boolean
  can_edit_connection?: boolean
  can_view_connection?: boolean
  can_view_connection_details?: boolean
  can_view_organization_metrics?: boolean
}

export interface UserScopePermissionsSchema {
  scope: string
  scope_id: string
  role: string
  permissions: UserPermissionSchema
}

export interface QueryBundleSchema extends QueryBundle {}

export interface ReasoningStepSchema {
  id: string
  content: string
  type: string
  args?: Record<string, unknown>
  result?: Record<string, unknown>
  start_at?: string
  end_at?: string
  token_usage?: ConversationTokenUsageSchema // Token usage for sub-agent calls
}

export interface AgentProfileSchema {
  id: string
  name: string
  system_prompt: string
  agent_type: AgentType
}

export interface ToolQueryDataSchema {
  tool_name: string
  provider: string
  arguments: Record<string, string>
}

export interface ConversationMetaSchema {
  reasoning_steps?: ReasoningStepSchema[]
  related_questions?: string[]
  agent_profile?: AgentProfileSchema
  assistant_response?: AssistantResponseSchema
  current_step_index?: number
  token_usage?: ConversationTokenUsageSchema
  response_time_ms?: number
  cost_credits?: number
  agent_version?: number
  agent_history_id?: string
  output_components?: OutputComponent[]
}

export interface AssistantResponseSchema {
  status: AssistantResponseStatus
  retryable?: boolean
  error?: AssistantResponseErrorSchema
}

export interface AssistantResponseErrorSchema {
  code: string
  message: string
  at: string
}

export interface TokenUsageSchema {
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  model?: string
  call_type?: string
}

export interface ConversationTokenUsageSchema {
  main_response?: TokenUsageSchema
  tool_calls?: TokenUsageSchema[]
  total_prompt_tokens: number
  total_completion_tokens: number
  total_tokens: number
  total_calls: number
}

export interface QueryRecordImageSchema {
  url: string
  hash: string
}

export interface QueryRecordSchema {
  id: number
  name: string
  url: string
  provider: string
  images: QueryRecordImageSchema[]
  content: string
  loc_start?: ChunkLocation
  loc_end?: ChunkLocation
  published_at: string
}

export interface UserQuerySchema {
  id: number
  sources: QuerySourceMetaSchema[]
  external_search: boolean
  records: QueryRecordSchema[]
}

export interface QuerySourceMetaSchema {
  id: string
  name: string
  provider: string
}

export interface WorkspaceSchema {
  id: string
  name: string
  default: boolean
  created_at: string
  updated_at: string
  owner: UserSchema
  my_permissions: UserScopePermissionsSchema
  members: UserSchema[]
  brand?: BrandSchema
  organization_id?: string
  api_key_clients_bypass_whitelist?: boolean
  mask_end_user_identity?: boolean
  contact_email?: string | null
  contact_email_default?: string | null
  monthly_end_user_credit_cap?: number | null
  end_user_credit_cap_block_message_i18n?: Partial<
    Record<"en" | "zh-TW" | "ja", string>
  > | null
  end_user_credit_cap_block_message_default_i18n?: Partial<
    Record<"en" | "zh-TW" | "ja", string>
  >
}

export interface ContextImageSchema {
  url: string
  name: string
  description?: string
  hash: string
}

export const transToContextImage = (
  schema: ContextImageSchema
): ContextImage => {
  return {
    url: schema.url,
    name: schema.name,
    description: schema.description,
    hash: schema.hash,
  }
}

export interface ContextObjSchema {
  id: number
  object_id: string
  connection_id?: number
  provider: string
  external_id?: string
  name: string
  url: string
  last_updated: string
  time_indexed: string
  content?: string
  images?: ContextImageSchema[]
}

export const transToContextObj = (schema: ContextObjSchema): ContextObj => {
  return {
    id: schema.id.toString(),
    name: schema.name,
    url: schema.url,
    connectionId: schema.connection_id?.toString() ?? "",
    provider: schema.provider,
    lastUpdated: schema.last_updated,
    timeIndexed: schema.time_indexed,
    content: schema.content,
    images: schema.images?.map(transToContextImage),
  }
}

export interface AgentMetaSchema {
  context_objects?: ContextObjSchema[]
  response_mode?: AgentResponseMode
  output_presentation_policy?: OutputPresentationPolicySchema
  ai_takeover_greeting?: string | null
}

export interface OutputPresentationPolicySchema {
  enable_output_presentations?: boolean
  allowed_presentation_kinds?: string[]
  max_items_by_kind?: Record<string, number>
  when_to_use_by_kind?: Record<string, string>
}

const KNOWN_OUTPUT_PRESENTATION_KINDS = new Set<string>(
  OUTPUT_PRESENTATION_KINDS.map(({ kind }) => kind)
)

const isOutputPresentationKind = (
  kind: string
): kind is OutputPresentationKind => KNOWN_OUTPUT_PRESENTATION_KINDS.has(kind)

export const transToOutputPresentationPolicy = (
  schema?: OutputPresentationPolicySchema
): OutputPresentationPolicy => {
  if (!schema) return { ...DEFAULT_OUTPUT_PRESENTATION_POLICY }

  const allowedPresentationKinds = (
    schema.allowed_presentation_kinds || []
  ).filter(isOutputPresentationKind)
  const allowedSet = new Set<string>(allowedPresentationKinds)

  return {
    enableOutputPresentations: schema.enable_output_presentations ?? false,
    allowedPresentationKinds,
    maxItemsByKind: Object.fromEntries(
      Object.entries(schema.max_items_by_kind || {})
        .filter(([kind]) => allowedSet.has(kind))
        .map(([kind, maxItems]) => [
          kind,
          clampOutputPresentationMaxItems(
            kind as OutputPresentationKind,
            maxItems
          ),
        ])
    ),
    whenToUseByKind: Object.fromEntries(
      Object.entries(schema.when_to_use_by_kind || {})
        .filter(
          ([kind, guidance]) =>
            allowedSet.has(kind) &&
            typeof guidance === "string" &&
            guidance.trim().length > 0
        )
        .map(([kind, guidance]) => [kind, guidance.trim()])
    ),
  }
}

export interface AgentLLMModelSettingsSchema {
  temperature?: number
  max_tokens?: number
  top_p?: number
  thinking?: AgentThinkingLevel
}

export const transToAgentLLMModelSettings = (
  schema: AgentLLMModelSettingsSchema
): AgentLLMModelSettings => {
  return {
    temperature: schema.temperature,
    maxTokens: schema.max_tokens,
    topP: schema.top_p,
    thinking: schema.thinking,
  }
}

export const transToAgentLLMModelSettingsMap = (
  schema?: Record<string, AgentLLMModelSettingsSchema>
): AgentLLMModelSettingsMap | undefined => {
  if (!schema) return undefined
  return Object.fromEntries(
    Object.entries(schema).map(([modelId, settings]) => [
      modelId,
      transToAgentLLMModelSettings(settings),
    ])
  )
}

export const transToBasicAgentMeta = (
  schema: AgentMetaSchema
): BasicAgentMeta => {
  return {
    contextObjects: schema.context_objects?.map(transToContextObj),
    outputPresentationPolicy: transToOutputPresentationPolicy(
      schema.output_presentation_policy
    ),
  }
}

export interface AgentSchema {
  id: string
  name: string
  description?: string
  creator?: UserSchema
  agent_type: AgentType
  workspace_id: string
  system_prompt: string
  unified_tools: UnifiedToolSchema[]
  suggested_questions?: string[]
  response_mode?: AgentResponseMode
  human_handoff?: HumanHandoffConfigSchema
  ai_takeover_greeting?: string | null
  use_search: boolean
  llm_model?: string
  llm_model_settings?: Record<string, AgentLLMModelSettingsSchema>
  meta: AgentMetaSchema
  created_at: string
  updated_at: string
  // New history-related fields
  version: number
  latest_version_number?: number
  published_version_number?: number
}

export interface HumanHandoffConfigSchema {
  enabled: boolean
  idle_timeout_minutes?: number | null
  handoff_instructions?: string | null
}

export const transToHumanHandoffConfig = (
  schema?: HumanHandoffConfigSchema
): HumanHandoffConfig => {
  if (!schema) return { ...DEFAULT_HUMAN_HANDOFF_CONFIG }
  return {
    enabled: schema.enabled ?? false,
    idleTimeoutMinutes:
      schema.idle_timeout_minutes === undefined
        ? undefined
        : schema.idle_timeout_minutes,
    handoffInstructions: schema.handoff_instructions ?? undefined,
  }
}

/**
 * Reads the AI takeover greeting from a top-level field when present, falling
 * back to the persisted AgentMeta value. Blank/missing means disabled ("").
 */
export const transToAiTakeoverGreeting = (source: {
  ai_takeover_greeting?: string | null
  meta?: { ai_takeover_greeting?: unknown } | null
}): string =>
  normalizeAiTakeoverGreeting(
    source.ai_takeover_greeting ?? source.meta?.ai_takeover_greeting
  )

export const transToAgent = (schema: AgentSchema): Agent => {
  return {
    id: schema.id,
    name: schema.name,
    description: schema.description,
    creator: schema.creator ? transToUser(schema.creator) : undefined,
    type: schema.agent_type,
    workspaceId: schema.workspace_id,
    systemPrompt: schema.system_prompt,
    unifiedTools: (schema.unified_tools || []).map(transToUnifiedTool),
    suggestedQuestions: schema.suggested_questions,
    responseMode: schema.response_mode ?? DEFAULT_AGENT_RESPONSE_MODE,
    humanHandoff: transToHumanHandoffConfig(schema.human_handoff),
    aiTakeoverGreeting: transToAiTakeoverGreeting(schema),
    useSearch: schema.use_search,
    llmModel: schema.llm_model || undefined,
    llmModelSettings: transToAgentLLMModelSettingsMap(
      schema.llm_model_settings
    ),
    outputPresentationPolicy: transToOutputPresentationPolicy(
      schema.meta?.output_presentation_policy
    ),
    meta: transToBasicAgentMeta(schema.meta),
    createdAt: schema.created_at,
    updatedAt: schema.updated_at,
    // New history-related fields
    version: schema.version,
    latestVersionNumber: schema.latest_version_number,
    publishedVersionNumber: schema.published_version_number,
  }
}

const transToHttpRequestTool = (
  schema?: HttpRequestToolSchema
): HttpRequestToolConfig | undefined => {
  if (!schema) return undefined
  return {
    method: schema.method,
    urlTemplate: schema.url_template,
    auth: {
      type: schema.auth.type,
      token: schema.auth.token,
      apiKey: schema.auth.api_key,
      apiKeyLocation: schema.auth.api_key_location,
      apiKeyParamName: schema.auth.api_key_param_name,
      username: schema.auth.username,
      password: schema.auth.password,
      headerName: schema.auth.header_name,
      headerValue: schema.auth.header_value,
    },
    headers: schema.headers ?? [],
    queryParams: schema.query_params ?? [],
    body: {
      template: schema.body.template,
      ...(schema.body.input_contracts === undefined
        ? {}
        : {
            inputContracts: Object.fromEntries(
              Object.entries(schema.body.input_contracts).map(
                ([key, contract]) => [
                  key,
                  {
                    type: contract.type,
                    format: contract.format,
                    jsonType: contract.json_type,
                  },
                ]
              )
            ),
          }),
    },
  }
}

const httpRequestToolToSchema = (
  config?: HttpRequestToolConfig
): HttpRequestToolSchema | undefined => {
  if (!config) return undefined
  return {
    method: config.method,
    url_template: config.urlTemplate,
    auth: {
      type: config.auth.type,
      token: config.auth.token,
      api_key: config.auth.apiKey,
      api_key_location: config.auth.apiKeyLocation,
      api_key_param_name: config.auth.apiKeyParamName,
      username: config.auth.username,
      password: config.auth.password,
      header_name: config.auth.headerName,
      header_value: config.auth.headerValue,
    },
    headers: config.headers ?? [],
    query_params: config.queryParams ?? [],
    body: {
      template: config.body.template,
      ...(config.body.inputContracts === undefined
        ? {}
        : {
            input_contracts: Object.fromEntries(
              Object.entries(config.body.inputContracts).map(
                ([key, contract]) => [
                  key,
                  {
                    type: contract.type,
                    format: contract.format,
                    json_type: contract.jsonType,
                  },
                ]
              )
            ),
          }),
    },
  }
}

const transToText2SpeechTool = (
  schema?: Text2SpeechToolSchema
): Text2SpeechConfig | undefined => {
  if (!schema) return undefined
  return {
    provider: schema.provider,
    voiceId: schema.voice_id,
    modelId: schema.model_id,
    languageCode: schema.language_code || undefined,
    outputFormat: schema.output_format,
  }
}

const text2SpeechToolToSchema = (
  config?: Text2SpeechConfig
): Text2SpeechToolSchema | undefined => {
  if (!config) return undefined
  return {
    provider: config.provider,
    voice_id: config.voiceId,
    model_id: config.modelId,
    language_code: config.languageCode,
    output_format: config.outputFormat,
  }
}

export const transToUnifiedTool = (schema: UnifiedToolSchema): UnifiedTool => {
  const draftPolicy =
    schema.draft_policy === "auto" ||
    schema.draft_policy === "approval_required"
      ? schema.draft_policy
      : undefined
  return {
    id: schema.id,
    type: schema.type,
    name: schema.name,
    description: schema.description,
    version: schema.version,
    domain: schema.domain,
    invocationInstruction: schema.invocation_instruction,
    draftPolicy,
    agentId: schema.agent_id,
    knowledgeNodeIds: schema.knowledge_node_ids,
    knowledgeNodeId: schema.knowledge_node_id,
    imageModel: schema.image_model,
    customFormSchema: schema.custom_form_schema,
    httpRequest: transToHttpRequestTool(schema.http_request),
    text2speech: transToText2SpeechTool(schema.text2speech),
  }
}

export const unifiedToolToSchema = (tool: UnifiedTool): UnifiedToolSchema => {
  return {
    id: tool.id,
    type: tool.type,
    name: tool.name,
    description: tool.description,
    version: tool.version,
    domain: tool.domain,
    invocation_instruction: tool.invocationInstruction,
    draft_policy: tool.draftPolicy,
    agent_id: tool.agentId,
    knowledge_node_ids: tool.knowledgeNodeIds,
    knowledge_node_id: tool.knowledgeNodeId,
    image_model: tool.imageModel,
    custom_form_schema: tool.customFormSchema,
    http_request: httpRequestToolToSchema(tool.httpRequest),
    text2speech: text2SpeechToolToSchema(tool.text2speech),
  }
}

export interface AppendedConversationSchema {
  id: number
  role: string
  content: string
}

export interface ProviderConfigSchema {
  domain?: string
  workspace_id?: string
}

export interface ConnectionSchema {
  id: number
  name: string
  provider: string
  provider_config?: ProviderConfigSchema
  status: string
  created_at: string
  updated_at: string
  last_connected_at?: string
  last_imported_at?: string
  owner_id?: string
  my_permissions?: UserScopePermissionsSchema
}

export interface ConnectionProfileSchema {
  id: number
  name: string
  provider: string
  status: string
}

export const transToQueryRecord = (schema: QueryRecordSchema): QueryRecord => {
  return {
    id: schema.id,
    name: schema.name,
    url: schema.url,
    provider: schema.provider,
    images: schema.images.map((image) => ({
      url: image.url,
      hash: image.hash,
    })),
    content: schema.content,
    locStart: schema.loc_start,
    locEnd: schema.loc_end,
    publishedAt: schema.published_at,
  } as QueryRecord
}

export const transToTokenUsage = (schema: TokenUsageSchema): TokenUsage => {
  return {
    promptTokens: schema.prompt_tokens,
    completionTokens: schema.completion_tokens,
    totalTokens: schema.total_tokens,
    model: schema.model,
    callType: schema.call_type,
  }
}

export const transToConversationTokenUsage = (
  schema: ConversationTokenUsageSchema
): ConversationTokenUsage => {
  return {
    mainResponse: schema.main_response
      ? transToTokenUsage(schema.main_response)
      : undefined,
    toolCalls: schema.tool_calls?.map(transToTokenUsage),
    totalPromptTokens: schema.total_prompt_tokens,
    totalCompletionTokens: schema.total_completion_tokens,
    totalTokens: schema.total_tokens,
    totalCalls: schema.total_calls,
  }
}

export const transToReasoningStep = (
  schema: ReasoningStepSchema
): ReasoningStep => {
  return {
    id: schema.id,
    content: schema.content,
    type: schema.type as ReasoningStepType,
    args: schema.args,
    result: schema.result,
    startTime: schema.start_at,
    endTime: schema.end_at,
    tokenUsage: schema.token_usage
      ? transToConversationTokenUsage(schema.token_usage)
      : undefined,
  }
}

export const transToAgentProfile = (
  schema: AgentProfileSchema
): AgentProfile => {
  return {
    id: schema.id,
    name: schema.name,
    systemPrompt: schema.system_prompt,
    type: schema.agent_type,
  } as AgentProfile
}

export const transToDefaultScope = (
  organizationId: string,
  schema: DefaultScopeSchema
): DefaultScope => {
  return {
    organizationId: organizationId,
    organizationRole: schema.organization_role,
    workspaceId: schema.workspace_id,
    workspaceRole: schema.workspace_role,
  } as DefaultScope
}

export const transToProfile = (schema: ProfileSchema): Profile => {
  return {
    defaultWorkspaceId: schema.default_workspace_id,
    defaultSearch: schema.default_search,
    defaultOrganizationId: schema.default_organization_id,
    defaultScopes: Object.fromEntries(
      Object.entries(schema.default_scopes).map(([orgId, scope]) => [
        orgId,
        transToDefaultScope(orgId, scope),
      ])
    ),
    preferredLLM: schema.preferred_llm,
    workspaceOrganizationMap: schema.workspace_organization_map,
  } as Profile
}

export const transToUser = (schema: UserSchema): User => {
  return {
    id: schema.id,
    name: schema.name,
    email: schema.email,
    avatar: schema.avatar,
    profile: schema.profile ? transToProfile(schema.profile) : undefined,
  } as User
}

export const transToCurrentUser = (schema: CurrentUserSchema): User => {
  return {
    ...transToUser(schema),
    hasUsablePassword: schema.has_usable_password,
    hasGoogleLogin: schema.has_google_login,
  } as User
}

export const transToMember = (schema: UserRoleSchema): Member => {
  return {
    id: schema.id,
    name: schema.name,
    email: schema.email,
    avatar: schema.avatar,
    profile: schema.profile ? transToProfile(schema.profile) : undefined,
    role: schema.my_permissions.role,
    permissions: new Set(transToPermissions(schema.my_permissions)),
  } as Member
}

export const transToPermissions = (
  schema: UserScopePermissionsSchema
): string[] => {
  const prefix = schema.scope
  const permissions: string[] = []
  Object.entries(schema.permissions).forEach(([key, value]) => {
    if (!value) return
    permissions.push(`${prefix}__${key}`)
  })
  return permissions
}

export const transToWorkspace = (schema: WorkspaceSchema): Workspace => {
  return {
    id: schema.id,
    name: schema.name,
    default: schema.default,
    owner: schema.owner ? transToUser(schema.owner) : NoOwner,
    isJoined: schema.my_permissions.role !== "undefined",
    myRole: schema.my_permissions.role,
    myPermissions: new Set(transToPermissions(schema.my_permissions)),
    createdAt: schema.created_at,
    updatedAt: schema.updated_at,
    members: schema.members
      ? schema.members.map((member) => transToUser(member))
      : [],
    brand: schema.brand ? transToBrand(schema.brand) : undefined,
    organizationId: schema.organization_id,
    apiKeyClientsBypassWhitelist: schema.api_key_clients_bypass_whitelist,
    maskEndUserIdentity: schema.mask_end_user_identity,
    contactEmail: schema.contact_email,
    contactEmailDefault: schema.contact_email_default,
    monthlyEndUserCreditCap: schema.monthly_end_user_credit_cap,
    endUserCreditCapBlockMessageI18n:
      schema.end_user_credit_cap_block_message_i18n,
    endUserCreditCapBlockMessageDefaultI18n:
      schema.end_user_credit_cap_block_message_default_i18n,
  } as Workspace
}

export const transToProviderConfig = (
  schema?: ProviderConfigSchema
): ProviderConfig | undefined => {
  if (!schema) return undefined
  return {
    domain: schema.domain,
    workspaceId: schema.workspace_id,
  }
}

export const providerConfigToProviderConfigSchema = (
  providerConfig?: ProviderConfig
): ProviderConfigSchema | undefined => {
  if (!providerConfig) return undefined
  return {
    domain: providerConfig.domain,
    workspace_id: providerConfig.workspaceId,
  }
}

export const transToConnection = (schema: ConnectionSchema): Connection => {
  return {
    id: schema.id.toString(),
    name: schema.name,
    provider: schema.provider,
    providerConfig: transToProviderConfig(schema.provider_config),
    status: schema.status,
    createdAt: schema.created_at,
    updatedAt: schema.updated_at,
    lastConnectedAt: schema.last_connected_at,
    lastImportedAt: schema.last_imported_at,
    ownerId: schema.owner_id,
    myPermissions: schema.my_permissions
      ? new Set(transToPermissions(schema.my_permissions))
      : undefined,
  }
}

export type HistoryMetaSchema = Record<string, never>

export interface AttachedFileSchema {
  id: string
  type: string
  name: string
  url: string
  scope: FileAttachmentScope
  attachment_type: string
  meta?: FileBadgeData["meta"]
}

// Evaluation schemas and transformation functions
export interface PreviousConversationsMetaSchema {
  source_history_id: number
  target_conversation_id: number
  previous_conversation_count: number
}

export interface EvaluationMetaSchema {
  previous_conversations?: PreviousConversationsMetaSchema
}

export interface CaseEvaluatorAssignmentSchema {
  evaluator_id: string
  rubric?: string
}

export interface EvaluationCaseLabelSchema {
  id: string
  workspace_id?: string
  name: string
  color: string
  created_at?: string
  updated_at?: string
}

export interface EvaluationCaseSchema {
  id: string
  agent_id: string
  workspace_id: string
  input: string
  attachments?: ApiAttachment[]
  labels?: EvaluationCaseLabelSchema[]
  expected_output?: string
  note?: string
  rubric?: string
  meta?: EvaluationMetaSchema
  created_at: string
  updated_at: string
}

export interface EvaluationCaseListSchema {
  cases: EvaluationCaseSchema[]
  total_case_count: number
}

export interface EvaluationCaseCountSchema {
  total_case_count: number
}

export const transToEvaluationCase = (
  schema: EvaluationCaseSchema
): EvaluationCase => {
  return {
    id: schema.id,
    agentId: schema.agent_id,
    workspaceId: schema.workspace_id,
    input: schema.input,
    attachments: apiAttachmentsToUploadingFiles(schema.attachments || []),
    labels: (schema.labels || []).map((label) => ({
      id: label.id,
      workspaceId: label.workspace_id,
      name: label.name,
      color: label.color,
      createdAt: label.created_at,
      updatedAt: label.updated_at,
    })),
    expectedOutput: schema.expected_output,
    note: schema.note,
    rubric: schema.rubric,
    meta: schema.meta
      ? {
          previousConversations: schema.meta.previous_conversations
            ? {
                sourceHistoryId:
                  schema.meta.previous_conversations.source_history_id,
                targetConversationId:
                  schema.meta.previous_conversations.target_conversation_id,
                previousConversationCount:
                  schema.meta.previous_conversations
                    .previous_conversation_count,
              }
            : undefined,
        }
      : undefined,
    createdAt: schema.created_at,
    updatedAt: schema.updated_at,
  }
}

export interface CaseEvaluatorInfosByCaseSchema {
  case_id: string
  evaluators: CaseEvaluatorAssignmentSchema[]
}

export const transToCaseEvaluatorInfosByCase = (
  schema: CaseEvaluatorInfosByCaseSchema
): CaseEvaluatorInfosByCase => ({
  caseId: schema.case_id,
  evaluators: schema.evaluators.map((assignment) => ({
    evaluatorId: assignment.evaluator_id,
    rubric: assignment.rubric,
  })),
})

export const transToEvaluationCases = (
  resp: EvaluationCaseSchema[]
): EvaluationCase[] => {
  return resp.map(transToEvaluationCase)
}

export interface EvaluationResultSchema {
  id: string
  evaluation_case_id: string
  evaluator_id: string
  operator?: UserSchema
  agent_history_id?: string
  status: string
  score?: number
  reason?: string
  output?: string
  output_updated_at?: string
  reasoning_steps?: ReasoningStepSchema[]
  execution_time?: number
  token_usage?: ConversationTokenUsageSchema // deprecated
  response_time_ms?: number // deprecated
  cost_credits?: number
  judge_llm_model_id?: string
  judge_llm_model_display_name?: string
  created_at: string
  updated_at: string
}

export const transToEvaluationResult = (
  schema: EvaluationResultSchema
): EvaluationResult => {
  return {
    id: schema.id,
    evaluationCaseId: schema.evaluation_case_id,
    evaluatorId: schema.evaluator_id,
    operator: schema.operator ? transToUser(schema.operator) : undefined,
    agentHistoryId: schema.agent_history_id,
    status: schema.status,
    score: schema.score,
    reason: schema.reason,
    output: schema.output,
    outputUpdatedAt: schema.output_updated_at,
    reasoningSteps: schema.reasoning_steps?.map(transToReasoningStep),
    executionTime: schema.execution_time,
    responseTimeMs: schema.response_time_ms,
    costCredits: schema.cost_credits,
    judgeLlmModelId: schema.judge_llm_model_id,
    judgeLlmModelDisplayName: schema.judge_llm_model_display_name,
    createdAt: schema.created_at,
    updatedAt: schema.updated_at,
  }
}

export const transToEvaluationResults = (
  resp: EvaluationResultSchema[]
): EvaluationResult[] => {
  return resp.map(transToEvaluationResult)
}

export interface RubricResponseSchema {
  case_id: string
  evaluator_id: string
  rubric: string
}

export interface BulkDeleteResponseSchema {
  message: string
  deleted_count: number
  [key: string]: unknown
}

export interface TriggerEvaluationResponseSchema {
  message: string
  triggered_count?: number
  created_results?: string[]
  skipped_cases?: string[]
  skipped_pairs?: SkippedEvaluationPairSchema[]
  [key: string]: unknown
}

export interface SkippedEvaluationPairSchema {
  case_id: string
  evaluator_id: string
  reason: string
}

// Evaluator schemas and transformation functions
export interface EvaluatorSchema {
  id: string
  name: string
  workspace_id: string
  system_prompt_template: string
  description?: string
  judge_llm_model_id?: string
  evaluation_result_count?: number
  created_by?: UserSchema
  created_at: string
  updated_at: string
}

export const transToEvaluator = (schema: EvaluatorSchema): Evaluator => {
  return {
    id: schema.id,
    name: schema.name,
    workspaceId: schema.workspace_id,
    systemPromptTemplate: schema.system_prompt_template,
    description: schema.description,
    judgeLlmModelId: schema.judge_llm_model_id,
    evaluationResultCount: schema.evaluation_result_count ?? 0,
    createdBy: schema.created_by ? transToUser(schema.created_by) : undefined,
    createdAt: schema.created_at,
    updatedAt: schema.updated_at,
  }
}

export const transToEvaluators = (resp: EvaluatorSchema[]): Evaluator[] => {
  return resp.map(transToEvaluator)
}

// Brand schemas and transformation functions
export interface FeedbackConfigSchema {
  description: string
  type_schema: Record<string, string>
  external_link: {
    url: string
    label: string
  }
}

export interface BrandSchema {
  id: string
  name: string
  description: string
  logo?: string
  feedback_config?: FeedbackConfigSchema
  workspace_count: number
  created_at: string
  updated_at: string
}

export interface BrandOAuthProviderSchema {
  id: string
  name: string
  provider: string
  client_id: string
  client_secret_preview: string
  scopes: string[]
  created_at: string
  updated_at: string
}

export const transToBrand = (schema: BrandSchema): Brand => {
  return {
    id: schema.id,
    name: schema.name,
    description: schema.description,
    logo: schema.logo,
    feedbackConfig: schema.feedback_config
      ? {
          description: schema.feedback_config.description,
          typeSchema: schema.feedback_config.type_schema,
          externalLink: {
            url: schema.feedback_config.external_link.url,
            label: schema.feedback_config.external_link.label,
          },
        }
      : undefined,
    workspaceCount: schema.workspace_count,
    createdAt: new Date(schema.created_at),
    updatedAt: new Date(schema.updated_at),
  }
}

export const transToBrands = (schemas: BrandSchema[]): Brand[] => {
  return schemas.map(transToBrand)
}

export const transToBrandOAuthProvider = (
  schema: BrandOAuthProviderSchema
): BrandOAuthProvider => {
  return {
    id: schema.id,
    name: schema.name,
    provider: schema.provider as ClientOAuthProvider,
    clientId: schema.client_id,
    clientSecretPreview: schema.client_secret_preview,
    scopes: schema.scopes,
    createdAt: new Date(schema.created_at),
    updatedAt: new Date(schema.updated_at),
  }
}

export const transToBrandOAuthProviders = (
  schemas: BrandOAuthProviderSchema[]
): BrandOAuthProvider[] => {
  return schemas.map(transToBrandOAuthProvider)
}

// ===== ClientChannel Schemas =====

export interface ClientChannelListSchema {
  id: string
  name?: string
  slug: string
  type: string
  allow_anonymous_access?: boolean
  agent_id?: string | null
  is_healthy?: boolean | null
  is_published?: boolean
  last_published_slug?: string
  last_published_version?: string
  published_at?: string
  created_at: string
  updated_at: string
}

export interface WebAdButtonSchema {
  text: string
  url: string
}

export interface ClientChannelImageMetaSchema {
  name: string
  slot: string
  path: string
  url: string
  size: number
  content_type: string
  uploaded_at: number
}

export interface WebClientConfigSchema {
  type?: "web"
  title?: string
  descriptions?: DescriptionItem[]
  allow_anonymous_access?: boolean
  valid_redeem_code?: string
  login_image?: ClientChannelImageMetaSchema
  header_image?: ClientChannelImageMetaSchema
  ad_buttons?: WebAdButtonSchema[]
}

export const transToWebClientConfig = (
  schema: WebClientConfigSchema
): WebClientConfig => {
  return {
    type: "web",
    title: schema.title || "",
    descriptions: schema.descriptions || [],
    allowAnonymousAccess: schema.allow_anonymous_access || false,
    validRedeemCode: schema.valid_redeem_code,
    loginImageUrl: schema.login_image?.url,
    headerImageUrl: schema.header_image?.url,
    adButtons:
      schema.ad_buttons?.map((button) => ({
        text: button.text,
        url: button.url,
      })) || [],
  }
}

export interface ExtensionClientConfigSchema {
  type?: "browser_extension"
  extension_id?: string
}

export interface WidgetUiConfigSchema {
  title?: string
  position?: WidgetPosition
  locale?: WidgetLocale
  launcher?: {
    label?: string
    icon?: ClientChannelImageMetaSchema
  }
  panel?: {
    greeting?: string
    suggestions?: string[]
  }
  theme?: {
    base_color?: WidgetBaseColor
  }
}

export interface WidgetClientConfigSchema {
  type?: "widget"
  agent_id?: string | null
  allow_anonymous_access?: boolean
  login_enabled?: boolean
  login_verification_mode?: WidgetLoginVerificationMode
  origin_policy_mode?: WidgetOriginPolicyMode
  allowed_origins?: string[]
  ui?: WidgetUiConfigSchema
}

export const transToWidgetClientConfig = (
  schema: WidgetClientConfigSchema
): WidgetClientConfig => {
  return {
    type: "widget",
    agentId: schema.agent_id || "",
    allowAnonymousAccess: schema.allow_anonymous_access ?? true,
    loginEnabled: schema.login_enabled ?? false,
    loginVerificationMode: schema.login_verification_mode ?? "assertion",
    originPolicyMode: schema.origin_policy_mode || "disabled",
    allowedOrigins: schema.allowed_origins || [],
    ui: {
      title: schema.ui?.title || "",
      position: schema.ui?.position || "bottom-right",
      locale: schema.ui?.locale || "en",
      launcher: {
        label: schema.ui?.launcher?.label ?? "Open chat",
        iconUrl: schema.ui?.launcher?.icon?.url,
      },
      panel: {
        greeting: schema.ui?.panel?.greeting,
        suggestions: schema.ui?.panel?.suggestions || [],
      },
      theme: {
        baseColor: schema.ui?.theme?.base_color || "default",
      },
    },
  }
}

export const transToExtensionClientConfig = (
  schema: ExtensionClientConfigSchema
): ExtensionClientConfig => {
  return {
    type: "browser_extension",
    extensionId: schema.extension_id || "",
  }
}

export interface SlackClientConfigSchema {
  type?: "slack"
  bot_token?: string
  signing_secret?: string
  team_id?: string
  team_name?: string
  bot_user_id?: string
  agent_id?: string | null
}

export const transToSlackClientConfig = (
  schema: SlackClientConfigSchema
): SlackClientConfig => {
  return {
    type: "slack",
    botToken: schema.bot_token || "",
    signingSecret: schema.signing_secret || "",
    teamId: schema.team_id || "",
    teamName: schema.team_name || "",
    botUserId: schema.bot_user_id,
    agentId: schema.agent_id || "",
  }
}

export interface LineClientConfigSchema {
  type?: "line"
  channel_secret?: string
  channel_access_token?: string
  liff_id?: string
  line_login_channel_id?: string
  agent_id?: string | null
  require_email_verification?: boolean
  skip_keywords?: string[]
  conversation_session_timeout_hours?: number
}

export const transToLineClientConfig = (
  schema: LineClientConfigSchema
): LineClientConfig => {
  return {
    type: "line",
    channelSecret: schema.channel_secret || "",
    channelAccessToken: schema.channel_access_token || "",
    liffId: schema.liff_id || "",
    lineLoginChannelId: schema.line_login_channel_id || "",
    agentId: schema.agent_id || "",
    requireEmailVerification: schema.require_email_verification ?? true,
    skipKeywords: schema.skip_keywords ?? [],
    conversationSessionTimeoutHours:
      schema.conversation_session_timeout_hours ?? 24,
  }
}

export interface MessengerClientConfigSchema {
  type?: "messenger"
  page_id?: string
  webhook_verify_token?: string
  meta_app_secret?: string
  access_token?: string
  agent_id?: string | null
  conversation_session_timeout_hours?: number
}

export const transToMessengerClientConfig = (
  schema: MessengerClientConfigSchema
): MessengerClientConfig => {
  return {
    type: "messenger",
    pageId: schema.page_id || "",
    webhookVerifyToken: schema.webhook_verify_token || "",
    metaAppSecret: schema.meta_app_secret || "",
    accessToken: schema.access_token || "",
    agentId: schema.agent_id || "",
    conversationSessionTimeoutHours:
      schema.conversation_session_timeout_hours ?? 24,
  }
}

export interface InstagramClientConfigSchema {
  type?: "instagram"
  account_id?: string
  webhook_verify_token?: string
  meta_app_secret?: string
  access_token?: string
  agent_id?: string | null
  conversation_session_timeout_hours?: number
}

export const transToInstagramClientConfig = (
  schema: InstagramClientConfigSchema
): InstagramClientConfig => {
  return {
    type: "instagram",
    accountId: schema.account_id || "",
    webhookVerifyToken: schema.webhook_verify_token || "",
    metaAppSecret: schema.meta_app_secret || "",
    accessToken: schema.access_token || "",
    agentId: schema.agent_id || "",
    conversationSessionTimeoutHours:
      schema.conversation_session_timeout_hours ?? 24,
  }
}

export type ClientChannelConfigSchema =
  | WebClientConfigSchema
  | WidgetClientConfigSchema
  | ExtensionClientConfigSchema
  | SlackClientConfigSchema
  | LineClientConfigSchema
  | MessengerClientConfigSchema
  | InstagramClientConfigSchema

export interface WidgetPrivateAuthSchema {
  issuer?: string
  has_shared_secret?: boolean
  secret_last_regenerated_at?: string
}

export interface ClientChannelSchema {
  id: string
  name?: string
  slug: string
  type: string
  workspace_id: string
  config?: ClientChannelConfigSchema
  widget_private_auth?: WidgetPrivateAuthSchema
  is_healthy?: boolean
  is_published?: boolean
  last_published_slug?: string
  last_published_version?: string
  published_at?: string
  publish_url: string
  created_at: string
  updated_at: string
}

export interface ClientChannelOAuthBindingSchema {
  id: string
  client_channel_id: string
  oauth_provider: BrandOAuthProviderSchema
  created_at: string
  updated_at: string
}

export const transToClientChannelListItem = (
  schema: ClientChannelListSchema
): ClientChannelListItem => {
  return {
    id: schema.id,
    name: schema.name || "",
    slug: schema.slug,
    type: schema.type as ClientChannelType,
    agentId: schema.agent_id || "",
    isHealthy: schema.is_healthy || false,
    isPublished: schema.is_published || false,
    lastPublishedSlug: schema.last_published_slug || "",
    lastPublishedVersion: schema.last_published_version || "",
    publishedAt: schema.published_at
      ? new Date(schema.published_at)
      : undefined,
    createdAt: new Date(schema.created_at),
    updatedAt: new Date(schema.updated_at),
  }
}

export const transToClientChannel = (
  schema: ClientChannelSchema
): ClientChannel => {
  let config: ClientChannelConfig = {} as ClientChannelConfig
  if (schema.config) {
    if (schema.type === "web") {
      config = transToWebClientConfig(schema.config as WebClientConfigSchema)
    } else if (schema.type === "widget") {
      config = transToWidgetClientConfig(
        schema.config as WidgetClientConfigSchema
      )
    } else if (schema.type === "browser_extension") {
      config = transToExtensionClientConfig(
        schema.config as ExtensionClientConfigSchema
      )
    } else if (schema.type === "slack") {
      config = transToSlackClientConfig(
        schema.config as SlackClientConfigSchema
      )
    } else if (schema.type === "line") {
      config = transToLineClientConfig(schema.config as LineClientConfigSchema)
    } else if (schema.type === "messenger") {
      config = transToMessengerClientConfig(
        schema.config as MessengerClientConfigSchema
      )
    } else if (schema.type === "instagram") {
      config = transToInstagramClientConfig(
        schema.config as InstagramClientConfigSchema
      )
    }
  }

  return {
    id: schema.id,
    name: schema.name || "",
    slug: schema.slug,
    type: schema.type as ClientChannelType,
    workspaceId: schema.workspace_id,
    config: config,
    widgetPrivateAuth: schema.widget_private_auth
      ? {
          issuer: schema.widget_private_auth.issuer,
          hasSharedSecret:
            schema.widget_private_auth.has_shared_secret || false,
          secretLastRegeneratedAt: schema.widget_private_auth
            .secret_last_regenerated_at
            ? new Date(schema.widget_private_auth.secret_last_regenerated_at)
            : undefined,
        }
      : undefined,
    isHealthy: schema.is_healthy || false,
    isPublished: schema.is_published || false,
    lastPublishedSlug: schema.last_published_slug || "",
    lastPublishedVersion: schema.last_published_version || "",
    publishUrl: schema.publish_url,
    publishedAt: schema.published_at
      ? new Date(schema.published_at)
      : undefined,
    createdAt: new Date(schema.created_at),
    updatedAt: new Date(schema.updated_at),
  }
}

export const transToClientChannelListItems = (
  schemas: ClientChannelListSchema[]
): ClientChannelListItem[] => {
  return schemas.map(transToClientChannelListItem)
}

export const transToClientChannelOAuthBinding = (
  schema: ClientChannelOAuthBindingSchema
): ClientChannelOAuthBinding => {
  return {
    id: schema.id,
    clientChannelId: schema.client_channel_id,
    oauthProvider: transToBrandOAuthProvider(schema.oauth_provider),
    createdAt: new Date(schema.created_at),
    updatedAt: new Date(schema.updated_at),
  }
}

export const transToClientChannelOAuthBindings = (
  schemas: ClientChannelOAuthBindingSchema[]
): ClientChannelOAuthBinding[] => {
  return schemas.map(transToClientChannelOAuthBinding)
}
